// Links between views (ADR-49). Every "open the thing this is about" link in
// the app is built here, so the URL contract for each page lives in one place.
//
// /runs       pipeline = expand + filter to that pipeline
//             run      = open that run's detail
//             last     = last-run status filter: failed | partial | success
//             day      = YYYY-MM-DD, runs started that UTC day
// /pipelines  q        = name search
//             status   = active | disabled | all
//             sched    = floating — schedules without pinned minutes (ADR-51)
//             edit     = open that pipeline's edit form (ADR-55)
//             table    = with edit: on the tables step, at that destination table

export interface RunsLink {
  pipeline?: string;
  run?: string;
  last?: 'failed' | 'partial' | 'success';
  day?: string;
}

const withParams = (path: string, params: Record<string, string | undefined>) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v) q.set(k, v);
  });
  const s = q.toString();
  return s ? `${path}?${s}` : path;
};

export const runsLink = (p: RunsLink = {}) => withParams('/runs', { ...p });

export const pipelinesLink = (
  p: { q?: string; status?: 'active' | 'disabled' | 'all'; sched?: 'floating'; edit?: string; table?: string } = {},
) => withParams('/pipelines', p);

/**
 * Where a notification should take you. `subject` is set by the backend's
 * detectors (services/notifications.py): `dataset.table` for quota,
 * `pipeline/table` for sustained failures, `pipeline` for stale runs,
 * `pipeline/tab` for spreadsheet changes.
 */
export function notificationLink(n: { kind: string; subject?: string | null }): string {
  const subject = n.subject ?? '';
  const pipeline = subject.split('/')[0];
  switch (n.kind) {
    case 'sustained_failure':
      return pipeline ? runsLink({ pipeline, last: 'failed' }) : runsLink({ last: 'failed' });
    case 'stale_run':
      return pipeline ? runsLink({ pipeline }) : runsLink();
    case 'sheet_drift':
      return pipeline ? pipelinesLink({ q: pipeline }) : pipelinesLink();
    case 'schedule_unpinned':
      return pipelinesLink({ status: 'active', sched: 'floating' });
    case 'schedule_request':
    case 'schedule_response':
      return '/settings/schedule-requests';
    case 'schedule_decision':
      // subject is "<pipeline>/<requester>"; the card has Accept / Reject.
      return pipeline ? pipelinesLink({ q: pipeline, status: 'all' }) : pipelinesLink();
    default:
      // quota, monitor health, run queue and pod memory: the Dashboard's
      // admin insights explain them.
      return '/dashboard';
  }
}
